import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import useAuth from '../../../hooks/useAuth';

function formatarListaParaCampo(valor) {
  if (Array.isArray(valor)) {
    return valor.map((item) => String(item).trim()).filter(Boolean).join(', ');
  }

  return typeof valor === 'string' ? valor : '';
}

const AlertasForm = ({ condominios, activeCondominio, onSubmit, editingAlerta, onCancelEdit }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    id_condominio: '',
    categoria: '',
    tipo: '',
    email: '',
    telegram_id: '',
    enviar_email: false,
    enviar_telegram: false,
    mensagem: '',
    config: {}
  });

  const [errors, setErrors] = useState({});
  const textareaRef = useRef(null);

  const condominioAtivoNome = useMemo(() => {
    const encontrado = (condominios || []).find(c => String(c.id) === String(activeCondominio));
    return encontrado?.nome || '';
  }, [condominios, activeCondominio]);

  // Placeholders disponíveis por tipo (baseado no backend)
  const placeholdersBase = [
    { token: '{tipo_alerta}', label: 'Tipo de Alerta' },
    { token: '{tipo_modulo}', label: 'Tipo de Módulo' },
    { token: '{relacionados}', label: 'Recursos Afetados' },
    { token: '{nome_condominio}', label: 'Nome do Condomínio' },
    { token: '{bloco}', label: 'Bloco' },
    { token: '{status}', label: 'Status' },
    { token: '{timestamp}', label: 'Data/Hora' }
  ];

  const placeholdersAdminOnly = [
    { token: '{comunicacao}', label: 'Tipo de Comunicação', adminOnly: true },
    { token: '{uid}', label: 'UID do Módulo', adminOnly: true },
    { token: '{channelnum}', label: 'Número do Canal', adminOnly: true }
  ];

  const placeholdersPorTipo = {
    reservatorio: [
      ...placeholdersBase,
      ...placeholdersAdminOnly,
      { token: '{nivel_atual}', label: 'Nível Atual (%)' },
      { token: '{nivel_minimo}', label: 'Nível Mínimo (%)' },
      { token: '{nivel_maximo}', label: 'Nível Máximo (%)' },
      { token: '{ultima_atualizacao}', label: 'Última Atualização' }
    ],
    conexao: [
      ...placeholdersBase,
      ...placeholdersAdminOnly,
      { token: '{tempo_offline}', label: 'Tempo Offline' },
      { token: '{ultima_atualizacao}', label: 'Última Atualização' }
    ],
    canal_inativo: [
      ...placeholdersBase,
      ...placeholdersAdminOnly,
      { token: '{tempo_offline}', label: 'Tempo Offline' },
      { token: '{ultima_atualizacao}', label: 'Última Atualização' }
    ],
    recuperacao_conexao: [
      ...placeholdersBase,
      ...placeholdersAdminOnly
    ],
    recuperacao_canal: [
      ...placeholdersBase,
      ...placeholdersAdminOnly
    ]
  };

  // Templates de mensagens para cada tipo de alerta
  const mensagemTemplates = {
    reservatorio: `🚨 *Alerta de nível de {tipo_alerta}*

  Condomínio: {nome_condominio}
  Recurso: {relacionados}
  Bloco: {bloco}

  Nível Atual: {nivel_atual}%
  Nível Mínimo: {nivel_minimo}%
  Nível Máximo: {nivel_maximo}%
  Status: {status}

  {timestamp}`,
    conexao: `⚠️ *Alerta de {tipo_alerta}*

  Condomínio: {nome_condominio}
  Tipo do Módulo: {tipo_modulo}
  Bloco: {bloco}

  Recursos Afetados: {relacionados}
  Tempo Offline: {tempo_offline}
  Status: {status}

  {timestamp}`,
    canal_inativo: `📡 *Alerta de canal inativo*

  Condomínio: {nome_condominio}
  Tipo do Módulo: {tipo_modulo}
  Bloco: {bloco}

  Recursos Afetados: {relacionados}
  Tempo Offline: {tempo_offline}
  Status: {status}

  {timestamp}`,
    recuperacao_conexao: `✅ *Recuperação de conexão*

  Condomínio: {nome_condominio}
  Tipo do Módulo: {tipo_modulo}
  Bloco: {bloco}

  Status: {status}
  Recursos Recuperados: {relacionados}

  {timestamp}`,
    recuperacao_canal: `✅ *Recuperação de canal*

  Condomínio: {nome_condominio}
  Tipo do Módulo: {tipo_modulo}
  Bloco: {bloco}

  Recursos Recuperados: {relacionados}
  Status: {status}

  {timestamp}`
  };

  // Configuração inicial baseada no tipo
  const configDefaults = {
    reservatorio: {},
    conexao: { timeout_minutos: 31 },
    canal_inativo: { timeout_minutos: 30 },
    recuperacao_conexao: {},
    recuperacao_canal: {}
  };

  // Campos dinâmicos baseados no tipo
  const configFields = {
    reservatorio: [],
    conexao: [
      { key: 'timeout_minutos', label: 'Timeout (minutos)', type: 'number', min: 1 }
    ],
    canal_inativo: [
      { key: 'timeout_minutos', label: 'Timeout (minutos)', type: 'number', min: 1 }
    ]
  };

  // ✅ Função para resetar formulário (deve estar antes do primeiro useEffect que o usa)
  const resetForm = useCallback(() => {
    setFormData({
      id_condominio: activeCondominio ? String(activeCondominio) : '',
      categoria: '',
      tipo: '',
      email: '',
      enviar_email: true,
      enviar_telegram: false,
      mensagem: '',
      config: {}
    });
    setErrors({});
  }, [activeCondominio]);

  // Preencher form quando estiver editando
  useEffect(() => {
    if (editingAlerta) {
      setFormData({
        id_condominio: editingAlerta.condominio_id || editingAlerta.id_condominio,
        categoria: editingAlerta.categoria,
        tipo: editingAlerta.tipo,
        email: formatarListaParaCampo(editingAlerta.email),
        telegram_id: formatarListaParaCampo(editingAlerta.telegram_id),
        enviar_email: editingAlerta.enviar_email,
        enviar_telegram: editingAlerta.enviar_telegram,
        mensagem: editingAlerta.mensagem,
        config: editingAlerta.config || {}
      });
    } else {
      resetForm();
    }
  }, [editingAlerta, resetForm]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    // Limpar erro do campo
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const handleCategoriaChange = (e) => {
    const categoria = e.target.value;
    setFormData({
      ...formData,
      categoria,
      tipo: '',
      mensagem: '',
      config: {}
    });
    setErrors({});
  };

  const handleTipoChange = (e) => {
    const tipo = e.target.value;
    const mensagem = mensagemTemplates[tipo] || '';
    const config = configDefaults[tipo] || {};
    
    setFormData({
      ...formData,
      tipo,
      mensagem,
      config
    });
    setErrors({});
  };

  const handleConfigChange = (key, value) => {
    setFormData({
      ...formData,
      config: {
        ...formData.config,
        [key]: parseFloat(value) || 0
      }
    });
  };

  const insertPlaceholder = (token) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = formData.mensagem;
    const before = text.substring(0, start);
    const after = text.substring(end);
    
    const newText = before + token + after;
    setFormData({ ...formData, mensagem: newText });
    
    // Restaurar foco e posição do cursor
    setTimeout(() => {
      textarea.focus();
      const newPos = start + token.length;
      textarea.setSelectionRange(newPos, newPos);
    }, 0);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.id_condominio) {
      newErrors.id_condominio = 'Condomínio ativo não definido';
    }
    if (!formData.categoria) {
      newErrors.categoria = 'Selecione uma categoria';
    }
    if (!formData.tipo) {
      newErrors.tipo = 'Selecione um tipo de alerta';
    }
    if (!formData.enviar_email && !formData.enviar_telegram) {
      newErrors.envio = 'Selecione pelo menos um método de envio';
    }
    if (formData.enviar_email && !formData.email) {
      newErrors.email = 'Digite o e-mail de destino';
    }
    if (formData.enviar_email && formData.email) {
      const emails = formData.email.split(',').map(e => e.trim()).filter(e => e);
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const emailsInvalidos = emails.filter(e => !emailRegex.test(e));
      if (emailsInvalidos.length > 0) {
        newErrors.email = `E-mail(s) inválido(s): ${emailsInvalidos.join(', ')}`;
      }
    }
    if (formData.enviar_telegram && !formData.telegram_id) {
      newErrors.telegram_id = 'Digite o Telegram ID';
    }
    if (formData.enviar_telegram && formData.telegram_id) {
      const telegramIds = formData.telegram_id.split(',').map(t => t.trim()).filter(t => t);
      const telegramInvalidos = telegramIds.filter((telegramId) => !/^-?\d+$/.test(telegramId));
      if (telegramInvalidos.length > 0) {
        newErrors.telegram_id = `Telegram ID(s) inválido(s): ${telegramInvalidos.join(', ')}`;
      }
    }
    if (!formData.mensagem.trim()) {
      newErrors.mensagem = 'Digite uma mensagem';
    }

    // Validações específicas por tipo
    // Reservatório não tem mais validação de config, pois usa os níveis da tabela reservatorios

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Converter emails e telegram_ids de string para array
    const submitData = {
      ...formData,
      email: formData.email ? formData.email.split(',').map(e => e.trim()).filter(e => e) : [],
      telegram_id: formData.telegram_id ? formData.telegram_id.split(',').map(t => t.trim()).filter(t => t) : []
    };

    const result = editingAlerta
      ? await onSubmit(editingAlerta.id, submitData)
      : await onSubmit(submitData);

    if (result.success) {
      resetForm();
      alert(editingAlerta ? 'Alerta atualizado com sucesso!' : 'Alerta criado com sucesso!');
    } else {
      alert('Erro: ' + result.error);
    }
  };

  const handleCancel = () => {
    resetForm();
    onCancelEdit();
  };

  // Opções de tipo baseadas na categoria
  const tiposDisponiveis = {
    monitoramento: [
      { value: 'reservatorio', label: 'Nível de Reservatório' },
      { value: 'conexao', label: 'Perda de Conexão' },
      { value: 'canal_inativo', label: 'Canal Inativo' }
    ],
    recuperacao: [
      { value: 'recuperacao_conexao', label: 'Recuperação de Conexão' },
      { value: 'recuperacao_canal', label: 'Recuperação de Canal' }
    ]
  };

  return (
    <form className="alertas-form" onSubmit={handleSubmit}>
      <div className="alertas-form-header">
        <h2>{editingAlerta ? 'Editar Alerta' : 'Novo Alerta'}</h2>
        <button type="button" onClick={handleCancel} className="alertas-modal-close">×</button>
      </div>

      <div className="alertas-form-grid">
        {/* Coluna 1 */}
        <div className="alertas-form-col">
          {/* Condomínio (segue o selector do header) */}
          <div className="alertas-form-group">
            <label>
              Condomínio <span className="alertas-required">*</span>
            </label>
            <div className="alertas-condominio-info">
              <span className="alertas-condominio-nome">
                {condominioAtivoNome || 'Condomínio não definido'}
              </span>
              <span className="alertas-condominio-hint">
                (selecionado no topo)
              </span>
            </div>
            {errors.id_condominio && <span className="alertas-error-text">{errors.id_condominio}</span>}
          </div>

          {/* Categoria */}
          <div className="alertas-form-group">
            <label htmlFor="categoria">
              Categoria <span className="alertas-required">*</span>
            </label>
            <select
              id="categoria"
              name="categoria"
              value={formData.categoria}
              onChange={handleCategoriaChange}
              className={errors.categoria ? 'alertas-input-error' : ''}
            >
              <option value="">Selecione uma categoria</option>
              <option value="monitoramento">Monitoramento</option>
              <option value="recuperacao">Recuperação</option>
            </select>
            {errors.categoria && <span className="alertas-error-text">{errors.categoria}</span>}
          </div>

          {/* Tipo (dinâmico baseado na categoria) */}
          {formData.categoria && (
            <div className="alertas-form-group">
              <label htmlFor="tipo">
                Tipo de Alerta <span className="alertas-required">*</span>
              </label>
              <select
                id="tipo"
                name="tipo"
                value={formData.tipo}
                onChange={handleTipoChange}
                className={errors.tipo ? 'alertas-input-error' : ''}
              >
                <option value="">Selecione um tipo</option>
                {tiposDisponiveis[formData.categoria]?.map(tipo => (
                  <option key={tipo.value} value={tipo.value}>
                    {tipo.label}
                  </option>
                ))}
              </select>
              {errors.tipo && <span className="alertas-error-text">{errors.tipo}</span>}
            </div>
          )}
        </div>

        {/* Coluna 2 */}
        <div className="alertas-form-col">
          {/* Configurações dinâmicas baseadas no tipo */}
          {formData.tipo && configFields[formData.tipo] && (
            <div className="alertas-form-group">
              <label>Configurações</label>
              <div className="alertas-config-fields">
                {configFields[formData.tipo].map(field => (
                  <div key={field.key} className="alertas-config-field">
                    <label htmlFor={field.key}>{field.label}</label>
                    <input
                      type={field.type}
                      id={field.key}
                      value={formData.config[field.key] || ''}
                      onChange={(e) => handleConfigChange(field.key, e.target.value)}
                      min={field.min}
                      max={field.max}
                    />
                  </div>
                ))}
              </div>
              {errors.config && <span className="alertas-error-text">{errors.config}</span>}
            </div>
          )}

          {/* Métodos de envio */}
          <div className="alertas-form-group">
            <label>
              Métodos de Envio <span className="alertas-required">*</span>
            </label>
            <div className="alertas-checkbox-group">
              <label className="alertas-checkbox-label">
                <input
                  type="checkbox"
                  name="enviar_email"
                  checked={formData.enviar_email}
                  onChange={handleInputChange}
                />
                📧 E-mail
              </label>
              <label className="alertas-checkbox-label">
                <input
                  type="checkbox"
                  name="enviar_telegram"
                  checked={formData.enviar_telegram}
                  onChange={handleInputChange}
                />
                📱 Telegram
              </label>
            </div>
            {errors.envio && <span className="alertas-error-text">{errors.envio}</span>}
          </div>

          {/* Email - aparece apenas se enviar_email está marcado */}
          {formData.enviar_email && (
            <div className="alertas-form-group">
              <label htmlFor="email">
                E-mail de Destino <span className="alertas-required">*</span>
              </label>
              <input
                type="text"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="exemplo@email.com, outro@email.com"
                className={errors.email ? 'alertas-input-error' : ''}
              />
              <small className="alertas-help-text">Separar múltiplos e-mails por vírgula</small>
              {errors.email && <span className="alertas-error-text">{errors.email}</span>}
            </div>
          )}

          {/* Telegram ID - aparece apenas se enviar_telegram está marcado */}
          {formData.enviar_telegram && (
            <div className="alertas-form-group">
              <label htmlFor="telegram_id">
                Telegram ID <span className="alertas-required">*</span>
              </label>
              <input
                type="text"
                id="telegram_id"
                name="telegram_id"
                value={formData.telegram_id}
                onChange={handleInputChange}
                placeholder="ID1, ID2, ID3"
                className={errors.telegram_id ? 'alertas-input-error' : ''}
              />
              <small className="alertas-help-text">Separar múltiplos IDs por vírgula</small>
              {errors.telegram_id && <span className="alertas-error-text">{errors.telegram_id}</span>}
            </div>
          )}
        </div>
      </div>

      {/* Mensagem - full width */}
      {formData.tipo && (
        <div className="alertas-form-group alertas-form-full">
          <label htmlFor="mensagem">
            Mensagem do Alerta <span className="alertas-required">*</span>
          </label>
          
          {/* Placeholders disponíveis */}
          {placeholdersPorTipo[formData.tipo] && (
            <div className="alertas-placeholders">
              <span className="alertas-placeholders-label">Clique para inserir:</span>
              <div className="alertas-placeholders-chips">
                {placeholdersPorTipo[formData.tipo]
                  .filter((ph) => user?.cargo === 'admin' || !ph.adminOnly)
                  .map((ph) => (
                  <button
                    key={ph.token}
                    type="button"
                    className="alertas-placeholder-chip"
                    onClick={() => insertPlaceholder(ph.token)}
                    title={`Inserir ${ph.label}`}
                  >
                    {ph.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <textarea
            ref={textareaRef}
            id="mensagem"
            name="mensagem"
            value={formData.mensagem}
            onChange={handleInputChange}
            rows="8"
            placeholder="Digite a mensagem do alerta..."
            className={errors.mensagem ? 'alertas-input-error' : ''}
          />
          {errors.mensagem && <span className="alertas-error-text">{errors.mensagem}</span>}
          <small className="alertas-help-text">
            Você pode usar Markdown. Os placeholders serão substituídos automaticamente ao enviar o alerta.
          </small>
        </div>
      )}

      {/* Botões */}
      <div className="alertas-form-actions">
        <button type="button" onClick={handleCancel} className="alertas-btn-cancel">
          Cancelar
        </button>
        <button type="submit" className="alertas-btn-submit">
          {editingAlerta ? 'Atualizar Alerta' : 'Criar Alerta'}
        </button>
      </div>
    </form>
  );
};

export default AlertasForm;
